import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { analytics, getStoredUTMParams } from '@/lib/analytics';
import { metaPixel, generateEventId } from '@/lib/metaPixel';
import { metaCAPI } from '@/lib/metaCAPI';
import { useToast } from '@/hooks/use-toast';
import { getStoredWhatsAppToken } from '@/hooks/useWhatsAppLeadToken';

export type LeadSource = 'contact' | 'brochure' | 'price' | 'callback' | 'floorplan' | 'floor_plan' | 'whatsapp_widget' | 'site_visit' | 'dossier' | 'exit_intent' | 'virtual_tour';
export type ContactChannel = 'phone' | 'email' | 'whatsapp';

interface FormData {
  name: string;
  email?: string;
  phone: string;
  honeypot?: string;
  contactChannel?: ContactChannel;
  engagementScore?: number;
  engagementLevel?: string;
  experimentId?: string;
}

interface UseLeadSubmitOptions {
  source: LeadSource;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface SubmitResult {
  success: boolean;
  leadId?: string;
}

interface UseLeadSubmitReturn {
  submitLead: (data: FormData) => Promise<SubmitResult>;
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
  reset: () => void;
}

export function useLeadSubmit({ source, onSuccess, onError }: UseLeadSubmitOptions): UseLeadSubmitReturn {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  const reset = useCallback(() => {
    setIsSuccess(false);
    setError(null);
  }, []);

  const submitLead = useCallback(async (data: FormData): Promise<SubmitResult> => {
    // Rate limiting (5 second cooldown)
    const now = Date.now();
    if (now - lastSubmitTime < 5000) {
      toast({
        title: 'Please wait',
        description: 'You can submit again in a few seconds.',
        variant: 'destructive',
      });
      return { success: false };
    }

    // Honeypot check
    if (data.honeypot && data.honeypot.trim() !== '') {
      return { success: false };
    }

    setIsSubmitting(true);
    setError(null);
    setLastSubmitTime(now);

    try {
      // Track form open
      analytics.leadOpenModal(source, window.location.pathname);

      // Prepare payload
      const utmParams = getStoredUTMParams();
      
      // Detect device type
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const deviceType = isMobile ? 'mobile' : 'desktop';
      
      // Determine contact channel
      const hasPhone = data.phone && data.phone.replace(/\D/g, '').length >= 10;
      const contactChannel = data.contactChannel || (hasPhone ? 'phone' : 'email');
      
      // Get WhatsApp token for reconciliation
      const waToken = getStoredWhatsAppToken();

      const payload = {
        name: data.name.trim(),
        email: data.email ? data.email.trim().toLowerCase() : '',
        phone: hasPhone ? data.phone.replace(/\D/g, '') : '',
        source,
        pagePath: window.location.pathname,
        consent: true, // Implicit consent via form submission with consent text
        utmSource: utmParams.utm_source || undefined,
        utmMedium: utmParams.utm_medium || undefined,
        utmCampaign: utmParams.utm_campaign || undefined,
        utmTerm: utmParams.utm_term || undefined,
        utmContent: utmParams.utm_content || undefined,
        referrer: document.referrer || undefined,
        userAgent: navigator.userAgent || undefined,
        contactChannel,
        waToken: waToken || undefined,
        engagementScore: data.engagementScore || undefined,
        engagementLevel: data.engagementLevel || undefined,
        experimentId: data.experimentId || undefined,
        metadata: {
          deviceType,
          ctaSource: source,
          timestamp: new Date().toISOString(),
        },
      };

      const { data: result, error: fnError } = await supabase.functions.invoke('submit-lead', {
        body: payload,
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to submit lead');
      }

      if (result?.error) {
        throw new Error(result.error);
      }

      // Track success - GA4 + Meta Pixel + CAPI (server-side)
      // Use shared eventId for deduplication between browser and server
      const eventId = generateEventId();
      
      analytics.leadSubmit(source, window.location.pathname);
      metaPixel.lead(
        { content_name: source, content_category: 'real_estate' },
        { eventID: eventId }
      );
      
      // Server-side tracking with CAPI for better match rate
      metaCAPI.lead({
        email: payload.email,
        phone: payload.phone,
        firstName: payload.name,
        contentName: source,
        contentCategory: 'real_estate',
        eventId,
      });

      setIsSuccess(true);
      setIsSubmitting(false);

      toast({
        title: 'Success!',
        description: 'Our team will contact you shortly.',
      });

      onSuccess?.();
      return { success: true, leadId: result?.leadId };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(errorMessage);
      setIsSubmitting(false);

      analytics.leadFormError(source, 'submission_error');

      toast({
        title: 'Something went wrong',
        description: errorMessage,
        variant: 'destructive',
      });

      onError?.(errorMessage);
      return { success: false };
    }
  }, [source, lastSubmitTime, toast, onSuccess, onError]);

  return { submitLead, isSubmitting, isSuccess, error, reset };
}
